import { default as MaterialTextField, StandardTextFieldProps } from '@material-ui/core/TextField';
import mergeLeft from 'ramda/src/mergeLeft';
import React from 'react';
import { isMobile } from 'react-device-detect';
import { FieldRenderProps } from 'react-final-form';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { Box } from '~common/components/box.component';
import { isNilOrEmpty } from '~common/helper/global';
import { LockIcon } from '~web/view/components/ui/icon/lock.icon';

export interface TextInputProps extends FieldRenderProps<string>, StandardTextFieldProps {
  hasLockIcon?: boolean;
  disableHelperText?: boolean;
  multiline?: boolean;
  executeOnBlur?: VoidFunction;
  executeOnFocus?: VoidFunction;
}

export const TextInput: React.FunctionComponent<TextInputProps> = ({
  children,
  input,
  meta,
  error,
  hasLockIcon,
  multiline,
  disableHelperText,
  helperText,
  InputProps,
  InputLabelProps,
  executeOnBlur,
  executeOnFocus,
  ...rest
}) => {
  const { name, onChange, value, onFocus, onBlur, onKeyDown } = input;
  const valueIsEmpty = isNilOrEmpty(value);
  const hidePlaceholder = isMobile && !meta.active && valueIsEmpty;
  const getHelperText = (): string =>
    meta.touched && !valueIsEmpty && !disableHelperText ? meta.error : undefined;
  const getError = (): boolean => meta.touched && meta.invalid;

  // Do not use onBlur and onFocus directly, otherwise the functions do not
  // trigger FinalForm state change correctly, instead, use executeOnBlur and executeOnFocus
  const onBlurHandler = (): void => {
    onBlur();
    executeOnBlur?.();
  };

  const onFocusHandler = (): void => {
    onFocus();
    executeOnFocus?.();
  };

  return (
    <Container className={'text-input-container'}>
      <StyledMaterialTextField
        helperText={disableHelperText ? undefined : helperText ?? getHelperText()}
        error={error || getError()}
        name={name}
        onFocus={onFocusHandler}
        onBlur={onBlurHandler}
        onChange={onChange}
        onKeyDown={onKeyDown}
        value={value}
        multiline={multiline}
        InputProps={mergeLeft(
          {
            style: {
              fontWeight: 'bold',
              fontSize: '18px',
              caretColor: 'white',
            },
          },
          InputProps,
        )}
        FormHelperTextProps={{ style: { whiteSpace: 'pre-wrap' } }}
        InputLabelProps={{
          style: {
            fontWeight: 'bold',
            fontSize: '16px',
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
          },
          ...InputLabelProps,
        }}
        hidePlaceholder={hidePlaceholder}
        {...rest}
      >
        {children}
      </StyledMaterialTextField>
      {hasLockIcon && <StyledLockIcon />}
    </Container>
  );
};

const Container = styled(Box)`
  width: 100%;
  // 34px field + 2px border under + 16px label + 12px helper + 8px margin for helper
  min-height: 72px;
`;

const StyledLockIcon = styled(LockIcon)`
  position: absolute;
  left: calc(100% - 26px);
  bottom: 30px;
`;

const StyledMaterialTextField = styled(({ hidePlaceholder, ...rest }) => (
  <MaterialTextField {...rest} />
))<{
  hidePlaceholder?: boolean;
}>`
  && > .MuiInputBase-root {
    ${(props): FlattenSimpleInterpolation | null =>
      props.hidePlaceholder &&
      css`
        color: ${props.theme.colors.transparent} !important;
      `};
  }
`;



// WEBPACK FOOTER //
// /codebuild/output/src426716145/src/modules/common/src/components/form/text-input.component.tsx